'use client'

import { type ReactNode, createContext, useContext, useEffect, useState } from 'react'

import { configPublic } from '@src/config.public'
import { log } from '@src/utils/log.util'

export type KnownPrivacyPurposeCode =
  'analytics' | 'essential_services' | 'personalization' | 'targeted_advertising'

export type KnownPrivacyPurposes = {
  [K in KnownPrivacyPurposeCode]: boolean
}

/** Known purposes are strongly typed; additional CMP purpose codes are allowed. */
export type UserPrivacyPurposes = KnownPrivacyPurposes & Record<string, boolean>

export type UserPrivacyPreferencesStatus = 'loading' | 'unavailable' | 'ready'

export type UserPrivacyPreferences = {
  status: UserPrivacyPreferencesStatus
  purposes: UserPrivacyPurposes
}

export const DEFAULT_PRIVACY_PURPOSES: UserPrivacyPurposes = {
  analytics: false,
  essential_services: true,
  personalization: false,
  targeted_advertising: false,
}

type KetchConsent = {
  purposes?: Record<string, boolean>
}

const UserPrivacyPreferencesContext = createContext<UserPrivacyPreferences | undefined>(undefined)

const mergePurposes = (consentPurposes?: Record<string, boolean>): UserPrivacyPurposes => ({
  ...DEFAULT_PRIVACY_PURPOSES,
  ...consentPurposes,
})

const namespace = '[PrivacyPreferences]'
export const UserPrivacyPreferencesProvider = ({ children }: { children: ReactNode }) => {
  const isKetchEnabled = configPublic.ketch.isEnabled
  const [preferences, setPreferences] = useState<UserPrivacyPreferences>(() => ({
    status: isKetchEnabled ? 'loading' : 'unavailable',
    purposes: { ...DEFAULT_PRIVACY_PURPOSES },
  }))

  useEffect(() => {
    if (!isKetchEnabled) return

    // Ketch may be absent, e.g. it is skipped in Builder edit mode.
    const ketch = window.ketch
    if (typeof ketch !== 'function') {
      log.debug(`${namespace} ketch unavailable`)
      setPreferences({
        status: 'unavailable',
        purposes: { ...DEFAULT_PRIVACY_PURPOSES },
      })
      return
    }

    const handleConsent = (consent: KetchConsent) => {
      log.debug(`${namespace} consent updated:`, consent)
      setPreferences({
        status: 'ready',
        purposes: mergePurposes(consent.purposes),
      })
    }

    ketch('on', 'consent', handleConsent)

    return () => {
      ketch('off', 'consent', handleConsent)
    }
  }, [isKetchEnabled])

  return (
    <UserPrivacyPreferencesContext.Provider value={preferences}>
      {children}
    </UserPrivacyPreferencesContext.Provider>
  )
}

export const useUserPrivacyPreferences = (): UserPrivacyPreferences => {
  const context = useContext(UserPrivacyPreferencesContext)
  if (!context) {
    throw new Error('useUserPrivacyPreferences must be used within UserPrivacyPreferencesProvider')
  }
  return context
}
